<template>
  <app-drawer
    drawer-ref="transfer-drawer"
    heading="Transfer details"
    :is-open="transferDrawerStore.isOpen"
    :click-outside-allow-classes="['transfers-table', 'statement-details-tabs']"
    :is-loading="false"
    @close="handleClose"
  >
    <template #body>
      <div class="space-y-8">
        <transfer-details-card
          :transfer="transfer?.data"
          :is-loading="isTransferPending"
        />
        <transfer-details-identifiers
          :transfer="transfer?.data"
          :is-loading="isTransferPending"
        />
        <transfer-details-recipient
          :transfer="transfer?.data"
          :transfer-recipient="transferRecipient?.data"
          :is-loading="isTransferPending || isTransferRecipientPending"
        />
      </div>
    </template>
  </app-drawer>
</template>

<script setup>
import { computed } from 'vue';
import { useRoute, useRouter, onBeforeRouteUpdate } from 'vue-router';
import { useTransferDrawerStore } from '@/stores/transfers';
import { usePollStatementById } from '@/services/statements/queries';
import { useTransferById } from '@/services/transfers/queries';
import { useTransferRecipientById } from '@/services/transfer-recipients/queries';
import { useQueryClient } from '@tanstack/vue-query';
import { StatementStatus } from '@/constants/statements.js';

import TransferDetailsCard from './TransferDetailsCard.vue';
import TransferDetailsIdentifiers from './TransferDetailsIdentifiers.vue';
import TransferDetailsRecipient from './TransferDetailsRecipient.vue';

const route = useRoute();
const router = useRouter();
const queryClient = useQueryClient();
const transferDrawerStore = useTransferDrawerStore();

const transferId = computed(() => transferDrawerStore.transferId);
const statementId = computed(() => route.params.statementId);

const enableTransferPolling = computed(
  () => transferDrawerStore.isOpen && statement?.value?.data.status === StatementStatus.FUNDED
);

const { data: statement } = usePollStatementById(statementId);
const { data: transfer, isPending: isTransferPending } = useTransferById(transferId, {
  enablePolling: enableTransferPolling,
});
const transferRecipientId = computed(() => transfer.value?.data?.recipient?.id || null);
const { data: transferRecipient, isPending: isTransferRecipientPending } =
  useTransferRecipientById(transferRecipientId);

const handleClose = () => {
  if (route.name === 'statement-details-transfer') {
    router.replace({ name: 'statement-details-transfers', params: { statementId: route.params.statementId } });
  }
  transferDrawerStore.closeDrawer();
  queryClient.invalidateQueries({ queryKey: ['transfer', transferId] });
};

onBeforeRouteUpdate((to, _from, next) => {
  if (transferDrawerStore.isOpen && to.name !== 'statement-details-transfer') {
    transferDrawerStore.closeDrawer();
  }
  next();
});
</script>
